import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../Sidebar/Sidebar";
import api from "../../api";
import "./UpdateBatch.css";

const UpdateBatch = ({ batchId }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const batchFromState = location.state?.batchData;

  const [batchData, setBatchData] = useState({
    expiration_date: "",
    quantity: "",
    nafdac_number: "",
  });

  // inventory_id is required by the backend to scope the batch lookup
  // unambiguously (batch_number alone is only unique per inventory_id,
  // not globally). Tracked separately from batchData since it's an
  // identifier, not an editable field.
  const [inventoryId, setInventoryId] = useState(
    batchFromState?.inventory_id || null
  );

  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const refs = {
    expiration_date: useRef(null),
    quantity: useRef(null),
    nafdac_number: useRef(null),
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Load batch details
  useEffect(() => {
    if (batchFromState) {
      setBatchData({
        expiration_date: batchFromState.expiration_date || "",
        quantity: batchFromState.quantity || "",
        nafdac_number: batchFromState.nafdac_number || "",
      });
      setInventoryId(batchFromState.inventory_id || null);
    } else if (batchId) {
      const fetchBatch = async () => {
        try {
          // ✅ Use api client without manual headers
          const response = await api.get(`/pharmacies/batches/${batchId}`);
          const data = response.data;
          setBatchData({
            expiration_date: data.expiration_date || "",
            quantity: data.quantity || "",
            nafdac_number: data.nafdac_number || "",
          });
          setInventoryId(data.inventory_id || null);
        } catch (err) {
          console.error("Failed to fetch batch:", err);
          setErrors({ form: "Failed to load batch details." });
        }
      };
      fetchBatch();
    }
  }, [batchId, batchFromState]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSuccess("");

    const newErrors = {};

    // expiration_date, quantity, and nafdac_number are all optional now —
    // only send/validate whichever fields the user actually filled in.
    if (batchData.quantity && parseInt(batchData.quantity) <= 0) {
      newErrors.quantity = "Quantity must be positive.";
    }

    // inventory_id is required by the backend — if it's missing here,
    // the request would 422 regardless of what else is filled in, so
    // surface this clearly instead of letting the request fail silently.
    if (!inventoryId) {
      newErrors.form =
        "Missing inventory reference for this batch — please go back and reopen it from the inventory list.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const firstField = Object.keys(newErrors)[0];
      refs[firstField]?.current?.scrollIntoView({ behavior: "smooth" });
      refs[firstField]?.current?.focus();
      return;
    }

    setLoading(true);
    try {
      // Only include fields the user actually set — all three are
      // optional on the backend now, so omit blanks rather than send
      // empty strings that could overwrite existing values.
      const payload = {};
      if (batchData.expiration_date.trim()) {
        payload.expiration_date = batchData.expiration_date;
      }
      if (batchData.quantity !== "") {
        payload.quantity = parseInt(batchData.quantity);
      }
      if (batchData.nafdac_number.trim()) {
        payload.nafdac_number = batchData.nafdac_number.trim().toUpperCase();
      }

      // ✅ inventory_id is sent as a query param — required by the backend
      // to scope the batch lookup unambiguously.
      await api.put(
        `/pharmacies/batches/${batchFromState?.batch_number || batchId}`,
        payload,
        { params: { inventory_id: inventoryId } }
      );

      setSuccess("✅ Batch updated successfully!");

      // Redirect to InventoryItem page after 1.5 seconds
      setTimeout(() => {
        navigate("/InventoryItem");
      }, 1500);
    } catch (err) {
      const message =
        err.response?.data?.detail || err.message || "❌ Failed to update batch.";
      setErrors({ form: message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="updatebatch-dashboard-container">
      {!isMobile && <Sidebar />}
      <div className="updatebatch-main-dashboard">
        <h2>✏️ Update Batch</h2>

        {errors.form && (
          <div className="updatebatch-error-message">{errors.form}</div>
        )}
        {success && (
          <div className="updatebatch-success-message">{success}</div>
        )}

        <form className="updatebatch-form" onSubmit={handleSubmit}>
          <label>Expiration Date (optional)</label>
          <input
            ref={refs.expiration_date}
            type="date"
            value={batchData.expiration_date}
            onChange={(e) =>
              setBatchData({ ...batchData, expiration_date: e.target.value })
            }
            min={new Date().toISOString().split("T")[0]}
            disabled={loading}
          />
          {errors.expiration_date && (
            <p className="updatebatch-error-message">
              {errors.expiration_date}
            </p>
          )}

          <label>Quantity (optional)</label>
          <input
            ref={refs.quantity}
            type="number"
            placeholder="Enter quantity"
            value={batchData.quantity}
            onChange={(e) =>
              setBatchData({ ...batchData, quantity: e.target.value })
            }
            disabled={loading}
          />
          {errors.quantity && (
            <p className="updatebatch-error-message">{errors.quantity}</p>
          )}

          <label>NAFDAC Number (optional)</label>
          <input
            ref={refs.nafdac_number}
            type="text"
            placeholder="Enter NAFDAC Number"
            value={batchData.nafdac_number}
            onChange={(e) =>
              setBatchData({ ...batchData, nafdac_number: e.target.value })
            }
            disabled={loading}
          />
          {errors.nafdac_number && (
            <p className="updatebatch-error-message">{errors.nafdac_number}</p>
          )}

          <button type="submit" disabled={loading}>
            {loading ? "Updating..." : "✅ Update Batch"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default UpdateBatch;